import { ICustomReport } from '@/Interfaces/ReportBuilder/CustomReport.interface';
import { ICustomReportListRequestPayload } from '@/Interfaces/ReportBuilder/RequestPayload.interface';
import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import { EXPAND_OPTIONS } from '@/constants/grid';
import { CustomReportDownloadFunction } from '@/types/ReportBuilder/GridFunction.types';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { GridPaginationModel, GridSortModel, gridClasses } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import GridToolBar from './Toolbar';
import { getColumns } from './columns';

interface IProps {
  loading: boolean;
  data: ICustomReport[];
  rowCount: number;
  loadGeneratedReports: (payload?: ICustomReportListRequestPayload) => void;
  onReportDownload: CustomReportDownloadFunction;
}

const GeneratedReportsGrid = ({ data, loading, rowCount, loadGeneratedReports, onReportDownload }: IProps) => {
  //#region sort and pagination
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 15,
  });

  const handlePaginationModelChange = useCallback((newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
  }, []);

  const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  }, []);

  const onRefresh = useCallback(() => {
    setPaginationModel(oldModel => ({ ...oldModel, page: 0 }));
  }, []);

  useEffect(() => {
    loadGeneratedReports({ page: paginationModel.page + 1, pageSize: paginationModel.pageSize, sort: sortModel });
  }, [loadGeneratedReports, paginationModel, sortModel]);
  //#endregion

  const columns = useMemo(() => getColumns(onReportDownload), [onReportDownload]);

  return (
    <Grid
      sx={{
        border: 'none',
        [`& .${gridClasses.main}`]: {
          borderBottom: '1px solid rgb(227, 227, 227)',
        },
        [`& .${gridClasses.columnHeaders}`]: {
          borderRadius: 0,
          borderTop: '1px solid rgb(227, 227, 227)',
        },
      }}
      columns={columns}
      loading={loading}
      autosizeOnMount
      autosizeOptions={EXPAND_OPTIONS}
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      showCellVerticalBorder
      showColumnVerticalBorder
      rows={data}
      columnHeaderHeight={30}
      sortingMode='server'
      paginationMode='server'
      rowCount={rowCount}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      pagination
      slots={{
        pagination: ServerSidePagination,
        toolbar: GridToolBar,
        columnSortedAscendingIcon: KeyboardArrowUp,
        columnSortedDescendingIcon: KeyboardArrowDown,
      }}
      slotProps={{
        pagination: {
          // @ts-expect-error pagination loading
          loading,
          rowCount,
        },
        footer: {
          className: 'relative',
        },
        toolbar: {
          loading,
          title: 'Generated Reports',
          onRefresh,
        },
      }}
    />
  );
};

export default GeneratedReportsGrid;
